import { useReducer } from "react"
import {
    initialState,
    addToCartInitiate,
    addToCartSuccess,
    addToCartFailure,
    updateCartInitiate,
    updateCartSuccess,
    updateCartFailure,
} from "./reducer"
import addToCartReducer from "./reducer.js"
import { useDispatch } from "react-redux"
import { directAxiosInstance } from "@lib/api"
import {
    updateCartData,
    parseCartInBriefResponseType,
    parseCartInFullResponseType,
    parseAddTestError,
    updateItemsInDoneInOneCart,
    parseItemsInDoneInOneCart,
    parseLabsCartInBriefResponseType,
    parseLabsCartInFullResponseType,
    updateLabsCartData,
    showConfirmDialog,
    showSnackBar,
} from "@containers/App/reducer"

import { DEFAULT_ERROR_MESSAGE } from "@containers/App/constants"
import { fetchUpsellData } from "@pages/CartPage/reducer.js"

/**
 * Function for adding an item to the cart
 * @param    {function} globalDispatch Function to dispatch an action in the global reducer.
 * @param    {function} localDispatch Function to dispatch an action in the local reducer.
 * @param    {object}   payload payload passed to the API call. {cart_id:"",page_source:"",sku_ids:[],types:""}
 * @param    {function} successCallback Callback function invoked when promise is resolved. Response is passed as argument to this successCallback
 * @param    {function} errorCallback Callback function invoked when promise is rejected. Error is passed as argument to this errorCallback
 * @return   {promise}
 */

const performAddToCartSequence = (globalDispatch, localDispatch, payload, successCallback, errorCallback) => {
    return new Promise((resolve, reject) => {
        localDispatch(addToCartInitiate())
        return directAxiosInstance()
            .post(`/api/v5/carts/skus`, payload)
            .then((response) => {
                const { upsell_data: upsellData, widgets: widgetData } = response?.data ?? {}
                if (upsellData && widgetData?.length > 0) {
                    globalDispatch(fetchUpsellData({ upsellData: upsellData, widgetData: widgetData }))
                }
                localDispatch(addToCartSuccess(true))
                // This action will be dispatched if types is doneInOne and will update itemsInDoneInOneCart in shell reducer
                if (payload?.types === "done_in_one") {
                    const cartData = parseItemsInDoneInOneCart(response)
                    globalDispatch(updateItemsInDoneInOneCart(cartData))
                } else {
                    if (payload?.response_type === "brief") {
                        // This action will only be dispatched when the response_type is brief which is the default case.
                        const cartData = parseCartInBriefResponseType(response)
                        globalDispatch(updateCartData(cartData))
                    } else if (payload?.response_type === "full") {
                        // This action will only be dispatched when the response_type is full.
                        const cartData = parseCartInFullResponseType(response)
                        globalDispatch(updateCartData(cartData))
                    }
                }
                if (typeof successCallback === "function") successCallback(response?.data)
                return resolve(response)
            })
            .catch((error) => {
                localDispatch(addToCartFailure())
                if (typeof errorCallback === "function") errorCallback(error)
                return reject(error)
            })
    })
}

/**
 * Function for updating an item in the cart
 * @param    {function} globalDispatch Function to dispatch an action in the global reducer.
 * @param    {function} localDispatch Function to dispatch an action in the local reducer.
 * @param    {object}   payload payload passed to the API call.  {cart_id:"",page_source:"",sku_ids:[],types:""}
 * @param    {function} successCallback Callback function invoked when promise is resolved. Response is passed as argument to this successCallback
 * @param    {function} errorCallback Callback function invoked when promise is rejected. Error is passed as argument to this errorCallback
 * @return   {promise}
 */

const performUpdateCartItemSequence = (
    globalDispatch,
    localDispatch,
    payload,
    successCallback,
    errorCallback
) => {
    const skuId = payload?.skus?.[0]?.sku_id
    delete payload["skus"]
    return new Promise((resolve, reject) => {
        localDispatch(updateCartInitiate())
        return directAxiosInstance()
            .put(`api/v5/carts/skus/${skuId}`, payload)
            .then((response) => {
                const { upsell_data: upsellData, widgets: widgetData } = response?.data ?? {}
                if (upsellData && widgetData?.length > 0) {
                    globalDispatch(fetchUpsellData({ upsellData: upsellData, widgetData: widgetData }))
                }
                localDispatch(updateCartSuccess())
                // This action will be dispatched if types is doneInOne and will update itemsInDoneInOneCart in shell reducer
                if (payload?.types === "done_in_one") {
                    const cartData = parseItemsInDoneInOneCart(response)
                    globalDispatch(updateItemsInDoneInOneCart(cartData))
                } else {
                    if (payload?.response_type === "brief") {
                        // This action will only be dispatched when the response_type is brief which is the default case.
                        const cartData = parseCartInBriefResponseType(response)
                        globalDispatch(updateCartData(cartData))
                    } else if (payload?.response_type === "full") {
                        // This action will only be dispatched when the response_type is full.
                        const cartData = parseCartInFullResponseType(response)
                        globalDispatch(updateCartData(cartData))
                    }
                }

                if (typeof successCallback === "function") successCallback(response?.data)
                return resolve(response)
            })
            .catch((error) => {
                localDispatch(updateCartFailure())
                if (typeof errorCallback === "function") errorCallback(error)
                return reject(error)
            })
    })
}

/**
 * Function for deleting an item in the cart
 * @param    {function} globalDispatch Function to dispatch an action in the global reducer.
 * @param    {function} localDispatch Function to dispatch an action in the local reducer.
 * @param    {object}   payload payload passed to the API call.  {cart_id:"",page_source:"",sku_ids:[],types:""}
 * @param    {function} successCallback Callback function invoked when promise is resolved. Response is passed as argument to this successCallback
 * @param    {function} errorCallback Callback function invoked when promise is rejected. Error is passed as argument to this errorCallback
 * @return   {promise}
 */

const performDeleteCartItemSequence = (
    globalDispatch,
    localDispatch,
    payload,
    successCallback,
    errorCallback
) => {
    return new Promise((resolve, reject) => {
        localDispatch(updateCartInitiate())
        return directAxiosInstance()
            .delete(`api/v5/carts/skus`, { data: payload })
            .then((response) => {
                const { upsell_data: upsellData, widgets: widgetData } = response?.data ?? {}
                if (upsellData && widgetData?.length > 0) {
                    globalDispatch(fetchUpsellData({ upsellData: upsellData, widgetData: widgetData }))
                }
                localDispatch(updateCartSuccess())
                // This action will be dispatched if types is doneInOne and will update itemsInDoneInOneCart in shell reducer
                if (payload?.types === "done_in_one") {
                    const cartData = parseItemsInDoneInOneCart(response)
                    globalDispatch(updateItemsInDoneInOneCart(cartData))
                } else {
                    if (payload?.response_type === "brief") {
                        // This action will only be dispatched when the response_type is brief which is the default case.
                        const cartData = parseCartInBriefResponseType(response)
                        globalDispatch(updateCartData(cartData))
                    } else if (payload?.response_type === "full") {
                        // This action will only be dispatched when the response_type is full.
                        const cartData = parseCartInFullResponseType(response)
                        globalDispatch(updateCartData(cartData))
                    }
                }
                if (typeof successCallback === "function") successCallback(response?.data)
                return resolve(response)
            })
            .catch((error) => {
                localDispatch(updateCartFailure())
                if (typeof errorCallback === "function") errorCallback(error)
                return reject(error)
            })
    })
}

/**
 * Function for adding an item to the labs cart
 * @param    {function} globalDispatch Function to dispatch an action in the global reducer.
 * @param    {function} localDispatch Function to dispatch an action in the local reducer.
 * @param    {object}   payload payload passed to the API call. {cart_id:"",source:"",sku_ids:[],types:""}
 * @param    {function} successCallback Callback function invoked when promise is resolved. Response is passed as argument to this successCallback
 * @param    {function} errorCallback Callback function invoked when promise is rejected. Error is passed as argument to this errorCallback
 * @return   {promise}
 */

const performAddToLabsCartSequence = (
    globalDispatch,
    localDispatch,
    payload,
    successCallback,
    errorCallback
) => {
    return new Promise((resolve, reject) => {
        localDispatch(addToCartInitiate())
        const apiUrl = "api/v4/carts/skus"
        let skuData = { id: payload?.sku_ids[0]?.test_id }
        if (payload.sku) {
            skuData = payload.sku
            delete payload.sku
        }
        return directAxiosInstance()
            .post(apiUrl, payload)
            .then((response) => {
                localDispatch(addToCartSuccess(true))
                if (payload?.response_type === "brief") {
                    // This action will only be dispatched when the response_type is brief which is the default case.
                    const cartData = parseLabsCartInBriefResponseType(response)
                    globalDispatch(updateLabsCartData(cartData))
                } else if (payload?.response_type === "full") {
                    // This action will only be dispatched when the response_type is full.
                    const cartData = parseLabsCartInFullResponseType(response)
                    globalDispatch(updateLabsCartData(cartData))
                }
                if (typeof successCallback === "function") successCallback(response?.data)
                return resolve(response)
            })
            .catch((err) => {
                localDispatch(addToCartFailure())
                if (typeof errorCallback === "function") errorCallback(err)
                if (err?.error?.dialog_data) {
                    const confirmConfig = {
                        showConfirmDialog: true,
                        sku: skuData,
                        ...parseAddTestError(err.error.dialog_data),
                    }
                    globalDispatch(showConfirmDialog(confirmConfig))
                } else {
                    showSnackBar(globalDispatch, err?.error?.message ?? DEFAULT_ERROR_MESSAGE)
                }

                return reject(err)
            })
    })
}

/**
 * Function for deleting an item in the labs cart
 * @param    {function} globalDispatch Function to dispatch an action in the global reducer.
 * @param    {function} localDispatch Function to dispatch an action in the local reducer.
 * @param    {object}   payload payload passed to the API call.  {cart_id:"",source:"",sku_ids:[],types:""}
 * @param    {function} successCallback Callback function invoked when promise is resolved. Response is passed as argument to this successCallback
 * @param    {function} errorCallback Callback function invoked when promise is rejected. Error is passed as argument to this errorCallback
 * @return   {promise}
 */

const performDeleteLabsCartItemSequence = (
    globalDispatch,
    localDispatch,
    payload,
    successCallback,
    errorCallback
) => {
    return new Promise((resolve, reject) => {
        localDispatch(updateCartInitiate())
        return directAxiosInstance()
            .delete(`api/v4/carts/skus`, { data: payload })
            .then((response) => {
                localDispatch(updateCartSuccess())
                if (payload?.response_type === "brief") {
                    // This action will only be dispatched when the response_type is brief which is the default case.
                    const cartData = parseLabsCartInBriefResponseType(response)
                    globalDispatch(updateLabsCartData(cartData))
                } else if (payload?.response_type === "full") {
                    // This action will only be dispatched when the response_type is full.
                    const cartData = parseLabsCartInFullResponseType(response)
                    globalDispatch(updateLabsCartData(cartData))
                }
                if (typeof successCallback === "function") successCallback(response?.data)
                return resolve(response)
            })
            .catch((error) => {
                localDispatch(updateCartFailure())
                if (typeof errorCallback === "function") errorCallback(error)
                return reject(error)
            })
    })
}

const useCartAPI = () => {
    // Using a global dispatch for multiple "Add to Cart" buttons can cause state conflicts, like one button triggering loading or success states on others.
    // To prevent this, we use localDispatch to manage state locally for each button, ensuring independent state updates.
    const [cartState, localDispatch] = useReducer(addToCartReducer, initialState)
    const globalDispatch = useDispatch()

    /**
     * Function for performing a cart action
     * @param    {String} type Type of action. Can have value "add_cart_item", "update_cart_item" or "delete_cart_item"
     * @param    {function} successCallback Callback function invoked when promise is resolved. Response is passed as argument to this successCallback
     * @param    {function} errorCallback Callback function invoked when promise is rejected. Error is passed as argument to this errorCallback
     * @return   {function}
     */

    const initiateCartAction = (type, payload, successCallback, errorCallback) => {
        if (type === "add_cart_item") {
            performAddToCartSequence(globalDispatch, localDispatch, payload, successCallback, errorCallback)
        } else if (type === "update_cart_item") {
            performUpdateCartItemSequence(
                globalDispatch,
                localDispatch,
                payload,
                successCallback,
                errorCallback
            )
        } else if (type === "delete_cart_item") {
            performDeleteCartItemSequence(
                globalDispatch,
                localDispatch,
                payload,
                successCallback,
                errorCallback
            )
        } else if (type === "add_labs_cart_item") {
            performAddToLabsCartSequence(
                globalDispatch,
                localDispatch,
                payload,
                successCallback,
                errorCallback
            )
        } else if (type === "delete_labs_cart_item") {
            performDeleteLabsCartItemSequence(
                globalDispatch,
                localDispatch,
                payload,
                successCallback,
                errorCallback
            )
        }
    }
    // this hook return cartState (which is the useReducer state associated with an instance of this hook), localDispatch to set values in this reducer and initiateCartAction function which can be used to perform add delete and update actions in cart
    return { cartState, localDispatch, initiateCartAction }
}

export default useCartAPI
